// Reusable Present/Complete/Data type/Sign rule table, used for both the
// LoanTape Columns and CashFlows Columns tabs of SettingsPage (they share the
// exact same ColumnRule shape - see PreflightSettings.loantape.columns /
// .cashflows.columns in compartment_report_calc_service/report_generation/preflight.py).
// Mirrors reporting-hub's own ColumnRulesTable.tsx field-for-field, just in
// PrimeReact instead of shadcn/ui.

import React from 'react';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

const DATA_TYPES = ['any', 'string', 'numeric', 'integer', 'date'];

// Encodes sign + include_zero into a single dropdown selection (matching
// reporting-hub's ColumnRulesTable.tsx) instead of a dropdown + separate
// checkbox - "incl. 0" is part of the choice itself, not a bolted-on extra.
const SIGN_OPTIONS = [
  { key: 'any', label: 'All', sign: 'any', include_zero: true },
  { key: 'positive:incl', label: 'Positive (incl. 0)', sign: 'positive', include_zero: true },
  { key: 'positive:excl', label: 'Positive (excl. 0)', sign: 'positive', include_zero: false },
  { key: 'negative:incl', label: 'Negative (incl. 0)', sign: 'negative', include_zero: true },
  { key: 'negative:excl', label: 'Negative (excl. 0)', sign: 'negative', include_zero: false },
];

const signOptionKey = (rule) => (rule.sign === 'any' ? 'any' : `${rule.sign}:${rule.include_zero ? 'incl' : 'excl'}`);

// Sign is only checkable when data_type yields numbers - mirrors the backend
// validator ColumnRule._sign_requires_numeric_data_type, which silently
// forces sign back to "any" otherwise.
const signAllowed = (dataType) => dataType === 'numeric' || dataType === 'integer';

const ColumnRulesTable = ({ rules, onChange }) => {
  const updateRow = (idx, patch) => {
    onChange(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const updateDataType = (idx, dataType) => {
    // Leaving numeric/integer makes the Sign criterion unenforceable - reset
    // it immediately instead of leaving a stale, inconsistent selection.
    updateRow(idx, signAllowed(dataType) ? { data_type: dataType } : { data_type: dataType, sign: 'any' });
  };

  const removeRow = (idx) => {
    onChange(rules.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    onChange([...rules, { column: '', required: false, not_null: false, data_type: 'any', sign: 'any', include_zero: true }]);
  };

  return (
    <div>
      <div className="cr-table-frame">
      <table className="cr-settings-table">
        <thead>
          <tr>
            <th>Column</th>
            <th className="cr-center" title="Column must be present">
              Present
            </th>
            <th className="cr-center" title="Column must not contain empty values">
              Complete
            </th>
            <th>Data type</th>
            <th title="Whether the value must always be positive, always negative, or either">
              Sign
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rules.map((rule, idx) => (
            <tr key={idx}>
              <td>
                <InputText value={rule.column} onChange={(e) => updateRow(idx, { column: e.target.value })} style={{ width: '100%' }} />
              </td>
              <td className="cr-center">
                <Checkbox checked={!!rule.required} onChange={(e) => updateRow(idx, { required: e.checked === true })} />
              </td>
              <td className="cr-center">
                <Checkbox checked={!!rule.not_null} onChange={(e) => updateRow(idx, { not_null: e.checked === true })} />
              </td>
              <td>
                <Dropdown
                  value={rule.data_type}
                  options={DATA_TYPES.map((t) => ({ label: t, value: t }))}
                  onChange={(e) => updateDataType(idx, e.value)}
                  style={{ width: '100%' }}
                />
              </td>
              <td>
                <Dropdown
                  value={signOptionKey(rule)}
                  options={SIGN_OPTIONS.map((o) => ({ label: o.label, value: o.key }))}
                  onChange={(e) => {
                    const option = SIGN_OPTIONS.find((o) => o.key === e.value);
                    if (option) updateRow(idx, { sign: option.sign, include_zero: option.include_zero });
                  }}
                  disabled={!signAllowed(rule.data_type)}
                  style={{ width: '100%' }}
                />
              </td>
              <td className="cr-right">
                <Button icon="pi pi-trash" className="p-button-danger p-button-text p-button-sm" onClick={() => removeRow(idx)} aria-label="Remove column" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <Button label="+ Add column" className="p-button-outlined p-button-sm" style={{ marginTop: '0.75rem' }} onClick={addRow} />
    </div>
  );
};

export default ColumnRulesTable;
